import React, { useState, useEffect, useRef } from "react";
import { /*Link,*/ useLocation, useNavigate  } from "react-router-dom";
import { format } from "date-fns";
import { useAppContext } from "../Contexts/AppContext";
import Constants from "../Utilities/Constants";
import ProductActions from "./ProductActions";
import ProductImage from "./ProductImage";

/**
 * Product Component
 * 
 * Displays a product card with image, details, price, and actions.
 * Handles unit switching via callback from ProductActions.
 * Re-enriches product data when cart changes or units are switched.
 * 
 * Props:
 * - product: Raw product data from API
 * - category: Product category for navigation
 * - subcategory: Product subcategory for navigation
 */
const Product = ({ product: rawProduct, category = "", subcategory = "" }) => {
    const { cart, enrichProduct } = useAppContext();
    const [product, setProduct] = useState(null);
    const [userSelectedUnitId, setUserSelectedUnitId] = useState(null); // Track user unit selection
    const location = useLocation();
    // Hooks used for modal/open navigation must be called unconditionally
    const thumbRef = useRef(null);
    const navigate = useNavigate();

    /**
     * Effect to enrich product data when rawProduct or cart changes
     * Preserves user-selected unit if available
     */
    useEffect(() => {
        if (rawProduct) {
            // Use user-selected unit if available, otherwise default enrichment
            const activeUnitId = userSelectedUnitId || null;
            const enrichedProduct = enrichProduct(rawProduct, activeUnitId);
            const cartitem = cart.find((p) => p.productId === enrichedProduct.id);
            enrichedProduct.cart = (cartitem) ? cartitem.itemQtty : 0;
            setProduct(enrichedProduct);
        }
    // eslint-disable-next-line
    }, [rawProduct, cart, enrichProduct]); // Note: userSelectedUnitId not in deps to avoid conflicts

    /**
     * Handle unit changes from ProductActions component
     * Rebuilds the enriched product with the selected unit as active
     * 
     * @param {number} unitId - ID of the selected unit
     */
    const handleUnitChange = (unitId) => {
        if (unitId !== undefined && unitId !== null) { // Allow unitId 0
            // Remember user's unit selection for future cart updates
            setUserSelectedUnitId(unitId);
            
            if (product && rawProduct) {
                // Rebuild enriched product with selected unit as active
                const freshEnrichedProduct = enrichProduct(rawProduct, unitId);
                const cartitem = cart.find((p) => p.productId === freshEnrichedProduct.id);
                freshEnrichedProduct.cart = (cartitem) ? cartitem.itemQtty : 0;
                
                setProduct(freshEnrichedProduct);
            }
        }
    };

    if (!product || !product.units) {
        return null;
    }

    const onOpen = (e) => {
        // Prevent default link behavior when using <a>
        e && e.preventDefault && e.preventDefault();

        // Read thumbnail bounding rect for shared-element animation
        const rect = thumbRef.current ? thumbRef.current.getBoundingClientRect() : null;

        navigate(`/product/${product.productUrl}`, {
            state: {
                modal: { backgroundLocation: location },
                category,
                subcategory,
                thumbRect: rect
            }
        });
    };

    return (
        <div className="Product shadow" id={product.id} key={product.id}>
            <div className="card h-100 text-center">
                <a href={`/product/${product.productUrl}`} onClick={onOpen} className="product-link">
                    <div ref={thumbRef}>
                        <ProductImage 
                            product={product}
                            className="card-img-top"
                            asBackground={true}
                        />
                    </div>
                </a>
               
                <div className="card-body">
                    <h5 className="card-title mb-0">{product.productName}</h5>
                    <div className="sale-text">{product.saleText}</div>
                                        <div className="sale-due">
                        {(product.saleDue != null) ? <span>בתוקף עד  {format(product.saleDue,'d/MM/yyyy')} </span> : <></>}
                    </div>
                    {(product.amount) &&
                    <>
                        <span className="small-text">
                            {(product.salePrice != null) ? 
                                <> {(product.salePrice/product.amount*100).toFixed(2)} </> 
                            :   <> {(product.price/product.amount*100).toFixed(2)} </>}

                        ל-100 {(product.amountUnit)}</span>
                    </>
                    }
                    
                    {product.soldout
                    ? <div className="card-text text-danger">המוצר אזל</div>
                    : <>
                        <div className="card-text">
                            {(product.salePrice != null)
                            ? <SalePrice product={product} activeUnit={product.unit} />
                            : <ItemPrice product={product} activeUnit={product.unit} />
                            }
                        </div>

                        <ProductActions
                            product={product}
                            showNotes={false}
                            onUnitChange={handleUnitChange}
                        />
                      </>
                    }
                </div>

                <div className="d-flex flex-row bd-highlight mb-3 labels">
                {product.labels && product.labels.map((label,idx) => {
                    return (                    
                        <img key={idx} className="me-md-1" src={`${Constants.IMAGE_URL}/${label.labelIcon}`} alt={label.labelName}/>
                    )
                })}
                </div>
                
                {product.cart > 0 ? <span className="cart-qtty">{product.cart}</span> : null}
                
                {product.description && 
                <div className="product-info">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-info-circle" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                    </svg>
                    <div className="details">
                        <p>{product.description}</p>
                    </div>
                 </div>
                }
            </div>
        </div>
    )
}

/**
 * Component for displaying item price (non-sale)
 */
const ItemPrice = ({ product, activeUnit }) => {
    if (!activeUnit) return <span>Loading...</span>;
    
    return (
        <>
        <span className="fw-bold px-1 fs-5">{Number.isInteger(product.price) ? product.price : product.price.toFixed(2)}</span>
        <span className="unit">{activeUnit.unitName}</span>
        </>
    )
}

/**
 * Component for displaying sale price with strikethrough original price
 */
const SalePrice = ({ product, activeUnit }) => {
    if (!activeUnit) return <span>Loading...</span>;
    
    return (
        <>
        <div>
            <span className="text-decoration-line-through">{Number.isInteger(product.price) ? product.price : product.price.toFixed(2)}</span>
            <span className="unit">{activeUnit.unitName}</span>
        </div>
        <div className="sale-price">
            <span className="bold">{Number.isInteger(product.salePrice) ? product.salePrice : product.salePrice.toFixed(2)} ₪</span>
        </div> 
        </>
    )
}


export default Product;